import { useNavigate } from "react-router-dom";
import { InsightCard } from "./adminShared";
import { Sparkles, TriangleAlert, CheckCircle2, ArrowRight, Scale, Database, FileWarning, ShieldCheck, Wallet, Users } from "lucide-react";

export default function Governance({ insights }) {
  const nav = useNavigate();
  if (!insights) return null;
  return (
    <div className="space-y-6" data-testid="admin-governance">
      {/* AI governance intelligence */}
      <div className="glass-card rounded-2xl p-6 space-y-4" data-testid="gov-summary">
        <div className="flex items-center gap-2 text-xs uppercase font-mono tracking-widest text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Governance intelligence
        </div>
        <p className="text-lg font-display leading-relaxed text-foreground/90">{insights.summary}</p>
        <div className="space-y-2 pt-1">
          {insights.priorities.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-card/50 px-4 py-2.5" data-testid={`gov-priority-${i}`}>
              <span className="flex items-center gap-2.5 text-sm">
                {p.level === "warning" ? <TriangleAlert className="h-4 w-4 text-amber-400 shrink-0" /> : <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />}
                {p.text}
              </span>
              {p.action && <button onClick={() => nav(p.action.url)} data-testid={`gov-priority-action-${i}`} className="text-xs text-primary hover:underline shrink-0 flex items-center gap-1">{p.action.label} <ArrowRight className="h-3 w-3" /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Insight-first cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InsightCard testId="insight-bias" icon={Scale} title="Bias & fairness"
          tone={insights.signals.bias_flagged_count ? "warn" : "ok"}
          metric={insights.signals.bias_flagged_count ? `${insights.signals.bias_flagged_count} flagged` : "All clear"}
          desc={insights.signals.bias_flagged_count ? `Review: ${insights.signals.bias_flagged.join(", ")}` : "No role breaches the four-fifths rule."}
          onOpen={() => nav("/admin/bias")} cta="Open bias monitor" />
        <InsightCard testId="insight-retention" icon={Database} title="Data retention"
          tone="ok" metric={`${insights.signals.retention_days} days`}
          desc="Candidate data auto-deletes after this window (GDPR)."
          onOpen={() => nav("/admin/settings")} cta="Adjust retention" />
        <InsightCard testId="insight-rights" icon={FileWarning} title="Data rights"
          tone={insights.signals.pending_rights ? "warn" : "ok"}
          metric={insights.signals.pending_rights ? `${insights.signals.pending_rights} pending` : "None pending"}
          desc="Subject-access & erasure requests awaiting a decision."
          onOpen={() => nav("/admin/audit")} cta="Review rights" />
        <InsightCard testId="insight-approval" icon={ShieldCheck} title="Human approval"
          tone={insights.signals.human_approval_required ? "ok" : "warn"}
          metric={insights.signals.human_approval_required ? "Enforced" : "OFF"}
          desc="AI never finalises a hiring decision without a human."
          onOpen={() => nav("/admin/settings")} cta="Review guardrails" />
        <InsightCard testId="insight-spend" icon={Wallet} title="AI spend"
          tone={insights.signals.spend_pct >= 80 ? "warn" : "ok"}
          metric={`${insights.signals.spend_pct}%`}
          desc={`$${insights.signals.ai_spend} of $${insights.signals.monthly_ai_budget} monthly budget`}
          onOpen={() => nav("/admin/settings")} cta="Manage AI" progress={insights.signals.spend_pct} />
        <InsightCard testId="insight-access" icon={Users} title="Access & roles"
          tone="ok" metric={`${insights.signals.users} users`}
          desc={`Scoring model: ${insights.signals.scoring_model || "—"}`}
          onOpen={() => nav("/admin/users")} cta="Manage users" />
      </div>
    </div>
  );
}
